import React, { useState } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import { Button } from "../widgets/Button.tsx";
import {
  Exercise,
  MuscleGroup,
  MuscleGroupWeight,
  MUSCLE_GROUP_LABELS,
} from "../../api/types.ts";
import { useDialog } from "../DialogManager.tsx";
import { isNotNullish } from "../../util/typeGuards.ts";

interface AdjustMuscleGroupWeightsDialogProps {
  open: boolean;
  closeDialog: () => void;
  exercise?: Exercise;
  primaryMuscleGroups: MuscleGroupWeight[];
  secondaryMuscleGroups: MuscleGroupWeight[];
  onSave: (
    primaryMuscleGroups: MuscleGroupWeight[],
    secondaryMuscleGroups?: MuscleGroupWeight[],
  ) => void;
}

const AdjustMuscleGroupWeightsDialog: React.FC<
  AdjustMuscleGroupWeightsDialogProps
> = ({
  open,
  closeDialog,
  exercise,
  primaryMuscleGroups,
  secondaryMuscleGroups,
  onSave,
}) => {
  const [adjustedPrimaryMuscleGroups, setAdjustedPrimaryMuscleGroups] =
    useState<MuscleGroupWeight[]>(primaryMuscleGroups);
  const [adjustedSecondaryMuscleGroups, setAdjustedSecondaryMuscleGroups] =
    useState<MuscleGroupWeight[]>(secondaryMuscleGroups);

  const { setError } = useDialog();

  const handleClose = () => {
    closeDialog();
  };

  const handleSave = () => {
    if (
      (adjustedPrimaryMuscleGroups.length +
        adjustedSecondaryMuscleGroups.length >
        1 &&
        adjustedPrimaryMuscleGroups.find((mg) => mg.weight < 0.01)) ||
      (adjustedSecondaryMuscleGroups.length !== 0 &&
        adjustedSecondaryMuscleGroups.find((mg) => mg.weight < 0.01))
    ) {
      setError(new Error("Weight cannot be less than 1%"));
      return;
    }

    const primarySum = adjustedPrimaryMuscleGroups.reduce(
      (prev, mg) => prev + mg.weight,
      0,
    );
    const secondarySum = adjustedSecondaryMuscleGroups.reduce(
      (prev, mg) => prev + mg.weight,
      0,
    );

    if (
      secondarySum >= primarySum ||
      adjustedPrimaryMuscleGroups.reduce(
        (prev, mg) => (mg.weight < prev ? mg.weight : prev),
        1.0,
      ) <=
        adjustedSecondaryMuscleGroups.reduce(
          (prev, mg) => (mg.weight > prev ? mg.weight : prev),
          0,
        )
    ) {
      setError(
        new Error(
          "Weights of secondary muscle groups should be less than weights of primary muscle groups",
        ),
      );
      return;
    }

    const sum = primarySum + secondarySum;

    if (sum < 0.1 || sum > 1.0) {
      setError(
        new Error(
          adjustedPrimaryMuscleGroups.length +
            adjustedSecondaryMuscleGroups.length >
            1
            ? "Sum of weights must be between 10% and 100%"
            : "Weight should be between 10% and 100%",
        ),
      );
      return;
    }

    onSave(adjustedPrimaryMuscleGroups, adjustedSecondaryMuscleGroups);
    handleClose();
  };

  const handlePrimaryChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    muscleGroup: MuscleGroup,
  ) => {
    setAdjustedPrimaryMuscleGroups((prev) => {
      return prev.map((mg) => {
        if (mg.muscle_group === muscleGroup) {
          return {
            muscle_group: muscleGroup,
            weight: Number(e.target.value) / 100,
          } as MuscleGroupWeight;
        }

        return mg;
      });
    });
  };

  const handleSecondaryChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    muscleGroup: MuscleGroup,
  ) => {
    setAdjustedSecondaryMuscleGroups((prev) => {
      return prev.map((mg) => {
        if (mg.muscle_group === muscleGroup) {
          return {
            muscle_group: muscleGroup,
            weight: Number(e.target.value) / 100,
          } as MuscleGroupWeight;
        }

        return mg;
      });
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={
        isNotNullish(exercise)
          ? `Adjust Muscle Group Weights for ${exercise.name}`
          : "Adjust Muscle Group Weights"
      }
    >
      <Dialog.Content>
        <p className="mb-4">
          Adjust the weights for each muscle group for improved performance
          estimations.
        </p>
        {adjustedSecondaryMuscleGroups.length > 0 && (
          <span className="my-1 italic">Primary Muscle Groups</span>
        )}
        {adjustedPrimaryMuscleGroups.map((muscleGroup) => (
          <TextInput
            value={
              muscleGroup.weight ? Math.round(muscleGroup.weight * 100) : ""
            }
            title={MUSCLE_GROUP_LABELS[muscleGroup.muscle_group!]}
            type="number"
            onChange={(e) => handlePrimaryChange(e, muscleGroup.muscle_group!)}
            rightElement={<span>%</span>}
          />
        ))}
        {adjustedSecondaryMuscleGroups.length > 0 && (
          <span className="border-t my-1 pt-2 italic">
            Secondary Muscle Groups
          </span>
        )}
        {adjustedSecondaryMuscleGroups.map((muscleGroup) => (
          <TextInput
            value={
              muscleGroup.weight ? Math.round(muscleGroup.weight * 100) : ""
            }
            title={MUSCLE_GROUP_LABELS[muscleGroup.muscle_group!]}
            type="number"
            onChange={(e) =>
              handleSecondaryChange(e, muscleGroup.muscle_group!)
            }
            rightElement={<span>%</span>}
          />
        ))}
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="neutral" onClick={closeDialog}>
          Cancel
        </Button>
        <Button variant="submit" onClick={handleSave}>
          Save
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { AdjustMuscleGroupWeightsDialog };
